import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Scale } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  COMMERCIAL_3_LEGAL_DOCUMENTS,
  LEGACY_SUPPLEMENTAL_LEGAL_DOCUMENTS,
  LEGAL_COMMERCIAL_3_VERSION,
  LEGAL_INFO_BANNER,
  LEGAL_PROVIDER_BNT,
  LEGAL_SYSTEM_COMMERCIAL_NAME,
  LICENSE_REQUEST_ACCEPTANCE_LABEL,
  LICENSE_REQUEST_LEGAL_BUNDLE,
  REGISTRATION_ACCEPTANCE_LABEL,
} from "@/modules/legal/legalDocumentsCatalog";

function DocumentMeta({ doc }) {
  return (
    <div className="text-sm text-slate-600 space-y-2 pb-4">
      <p>{doc.summary}</p>
      {doc.commercial3 ? (
        <div className="rounded-md border border-[#0F2D5C]/10 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-1">
          <p>
            <span className="font-medium">Versión:</span> {doc.version || LEGAL_COMMERCIAL_3_VERSION}
          </p>
          {doc.sourcePath ? (
            <p>
              <span className="font-medium">Fuente:</span>{" "}
              <code className="text-[10px]">{doc.sourcePath}</code>
            </p>
          ) : null}
          {doc.hash ? (
            <p className="break-all">
              <span className="font-medium">SHA-256:</span>{" "}
              <code className="text-[10px]">{doc.hash}</code>
            </p>
          ) : null}
          <p className="text-slate-500">
            Texto completo disponible en el repositorio ({doc.sourcePath}). La aceptación electrónica
            persistida se habilitará en Comercial 3C.
          </p>
        </div>
      ) : (
        <p className="text-slate-800 text-xs">
          Documento complementario — contenido formal pendiente de publicación en fase posterior.
        </p>
      )}
      <p className="text-xs text-slate-500">Requerido en: {doc.requiredAt.join(", ")}</p>
    </div>
  );
}

export default function LegalDocumentosHub() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="legal-documentos-hub">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0F2D5C]"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al login
          </Link>
        </div>

        <div className="flex items-start gap-3">
          <Scale className="w-8 h-8 text-[#0F2D5C] shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-semibold text-[#0F2D5C]">Documentos legales y comerciales</h1>
            <p className="text-sm text-slate-600 mt-1">
              {LEGAL_SYSTEM_COMMERCIAL_NAME} — versión{" "}
              <span className="font-mono">{LEGAL_COMMERCIAL_3_VERSION}</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {LEGAL_PROVIDER_BNT.legal_name} · RFC {LEGAL_PROVIDER_BNT.rfc}
            </p>
          </div>
        </div>

        <Alert className="border-sky-200 bg-sky-50 text-sky-950">
          <FileText className="h-4 w-4" />
          <AlertTitle>Vista informativa</AlertTitle>
          <AlertDescription>{LEGAL_INFO_BANNER}</AlertDescription>
        </Alert>

        <div className="rounded-lg border bg-white p-4 text-sm text-slate-700 space-y-3">
          <p className="font-medium text-slate-900">Bundle solicitud de licencia (Comercial 3A)</p>
          <p className="text-xs text-slate-600">
            Documentos base para aceptación futura al enviar solicitud:{" "}
            {LICENSE_REQUEST_LEGAL_BUNDLE.join(", ")}.
          </p>
          <p className="text-slate-600">{LICENSE_REQUEST_ACCEPTANCE_LABEL}</p>
          <p className="text-xs text-slate-500">
            Sin checkbox obligatorio ni bitácora en esta fase. Registro SaaS mantiene texto legacy:{" "}
            {REGISTRATION_ACCEPTANCE_LABEL}
          </p>
        </div>

        <section>
          <h2 className="text-sm font-semibold text-[#0F2D5C] mb-2">
            Documentos base — {LEGAL_SYSTEM_COMMERCIAL_NAME} v{LEGAL_COMMERCIAL_3_VERSION}
          </h2>
          <Accordion type="single" collapsible className="bg-white rounded-lg border px-2">
            {COMMERCIAL_3_LEGAL_DOCUMENTS.map((doc) => (
              <AccordionItem key={doc.id} value={doc.id}>
                <AccordionTrigger className="text-left text-[#0F2D5C] hover:no-underline">
                  {doc.title}
                </AccordionTrigger>
                <AccordionContent>
                  <DocumentMeta doc={doc} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Documentos complementarios</h2>
          <Accordion type="single" collapsible className="bg-white rounded-lg border px-2">
            {LEGACY_SUPPLEMENTAL_LEGAL_DOCUMENTS.map((doc) => (
              <AccordionItem key={doc.id} value={doc.id}>
                <AccordionTrigger className="text-left text-slate-700 hover:no-underline">
                  {doc.title}
                </AccordionTrigger>
                <AccordionContent>
                  <DocumentMeta doc={doc} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </div>
  );
}
